import { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { loginSchema, LoginSchema } from './authSchema';
import { loginUser } from './authService';
import { startKakaoLogin, startNaverLogin } from './socialAuth';
import { useUserSession } from './userSession';
import styles from './Auth.module.scss';

function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [session, setSession] = useUserSession();
  const [serverError, setServerError] = useState<string | null>(null);
  const [socialLoading, setSocialLoading] = useState<'kakao' | 'naver' | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginSchema>({ resolver: zodResolver(loginSchema) });

  const loginState = location.state as { from?: string; fromState?: unknown } | null;
  const redirectTo = loginState?.from ?? '/mypage';
  // 주문서 작성 중 로그인한 경우 선택했던 상품 정보(state)를 그대로 돌려준다
  const redirectState = loginState?.fromState;

  useEffect(() => {
    if (session) {
      navigate(redirectTo, { replace: true, state: redirectState });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  const onSubmit = async (values: LoginSchema) => {
    setServerError(null);
    try {
      const { token, user } = await loginUser(values);
      setSession({ token, user });
      navigate(redirectTo, { replace: true, state: redirectState });
    } catch (err) {
      setServerError(err instanceof Error ? err.message : '로그인 중 오류가 발생했습니다.');
    }
  };

  const handleKakaoLogin = async () => {
    setServerError(null);
    setSocialLoading('kakao');
    try {
      await startKakaoLogin();
    } catch (err) {
      setServerError('카카오 로그인을 시작할 수 없습니다.');
      setSocialLoading(null);
    }
  };

  const handleNaverLogin = async () => {
    setServerError(null);
    setSocialLoading('naver');
    try {
      await startNaverLogin();
    } catch (err) {
      setServerError('네이버 로그인을 시작할 수 없습니다.');
      setSocialLoading(null);
    }
  };

  return (
    <div className={`${styles.page} ${styles.pageCenter}`}>
      <section className={styles.pageHead}>
        <div className={styles.crumb}>
          <Link to="/">HOME</Link>
          <span>/</span>
          <span>로그인</span>
        </div>
        <h1>로그인</h1>
        <p>회원 로그인 후 견적 문의와 마이페이지를 이용할 수 있습니다.</p>
      </section>

      <section className={styles.formCard}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className={styles.field}>
            <label htmlFor="login-email">이메일</label>
            <input id="login-email" type="email" placeholder="hello@brand.com" {...register('email')} />
            {errors.email && <p className={styles.fieldError}>{errors.email.message}</p>}
          </div>
          <div className={styles.field}>
            <label htmlFor="login-password">비밀번호</label>
            <input id="login-password" type="password" {...register('password')} />
            {errors.password && <p className={styles.fieldError}>{errors.password.message}</p>}
          </div>

          {serverError && <p className={styles.errorBox}>{serverError}</p>}

          <button type="submit" className={styles.submitBtn} disabled={isSubmitting}>
            {isSubmitting ? '로그인 중...' : '로그인'}
          </button>
        </form>

        <div className={styles.socialDivider}>또는</div>
        <div className={styles.socialButtons}>
          <button
            type="button"
            className={`${styles.socialBtn} ${styles.kakaoBtn}`}
            onClick={handleKakaoLogin}
            disabled={socialLoading !== null}
          >
            {socialLoading === 'kakao' ? '이동 중...' : '카카오로 로그인'}
          </button>
          <button
            type="button"
            className={`${styles.socialBtn} ${styles.naverBtn}`}
            onClick={handleNaverLogin}
            disabled={socialLoading !== null}
          >
            {socialLoading === 'naver' ? '이동 중...' : '네이버로 로그인'}
          </button>
        </div>

        <div className={styles.helpLinks}>
          <Link to="/signup">아직 회원이 아니신가요? 회원가입</Link>
        </div>
      </section>
    </div>
  );
}

export default Login;
